import { useEffect, useMemo, useState } from "react";
import { ethers } from "ethers";
import {
  ContentBox,
  LoadingSkeleton,
  SoilBar,
  Typography,
  UsdcBar,
} from "../../../../components";
import { config } from "../../../../config";
import { useAppSelector } from "../../../../redux";
import { formatBigNumber } from "../../../../utils";
import { UserRewards } from "./UserRewards";
import * as S from "./UserInvestmentsInfo.styled";
import { useFetchStakingData } from "../../Stake/useFetchStakingData";
import { api } from "../../../../api";
import { toast } from "../../../../toasts";
import { getUserTotalInvested } from "../../../../api/pools";

type GlobalPoolStats = {
  totalLend: string;
  loansValue: string;
};

export const UserInvestmentsInfo = () => {
  const { isLogged } = useAppSelector((state) => state.user);
  const [userLent, setUserLent] = useState<string | number>(0);
  const [stats, setStats] = useState<AsyncDataState<GlobalPoolStats>>({
    state: "LOADING",
  });

  const getUserTotalLent = async () => {
    try {
      const res = await getUserTotalInvested();
      setUserLent(
        ethers.utils.formatUnits(
          res.data.totalInvested,
          config.USDC_TOKEN_DECIMALS
        )
      );
    } catch (e: unknown) {
      toast.errorHandler(e, "Unable fetch user total invested");
    }
  };

  const fetchGlobalPoolStats = async () => {
    try {
      setStats({ state: "LOADING" });
      const {
        data: { totalDeposited, totalHardCaps },
      } = await api.pools.getGlobalPoolStats();

      setStats({
        state: "SUCCESS",
        data: {
          totalLend: formatBigNumber(
            totalDeposited,
            config.USDC_TOKEN_DECIMALS
          ),
          loansValue: formatBigNumber(
            totalHardCaps,
            config.USDC_TOKEN_DECIMALS
          ),
        },
      });
    } catch (e: unknown) {
      toast.errorHandler(e, "Failed to fetch pool data");
    }
  };

  useEffect(() => {
    fetchGlobalPoolStats();
  }, []);

  useEffect(() => {
    if (isLogged) getUserTotalLent();
  }, [isLogged]);

  useFetchStakingData();
  const { myStaked } = useAppSelector((state) => state.staking);
  const { price } = useAppSelector((state) => state.token);
  const myStakedInUsdc = useMemo(
    () =>
      Number(formatBigNumber(myStaked, config.SOIL_TOKEN_DECIMALS)) *
      Number(price),
    [myStaked, price]
  );

  return (
    <S.Content>
      <S.FirstSection>
        <S.BoxWithTitle>
          <Typography.IngraSubtitle fontWeight={600}>
            TOTAL LENT
          </Typography.IngraSubtitle>
          <ContentBox>
            {stats.state === "SUCCESS" ? (
              <UsdcBar value={userLent || 0} />
            ) : (
              <LoadingSkeleton width="80px" />
            )}
          </ContentBox>
        </S.BoxWithTitle>
        <S.BoxWithTitle>
          <Typography.IngraSubtitle fontWeight={600}>
            IN GROW
          </Typography.IngraSubtitle>
          <ContentBox>
            <S.BoxInGrow>
              <UsdcBar value={myStakedInUsdc || 0} />
              <SoilBar
                value={
                  myStaked
                    ? formatBigNumber(myStaked, config.SOIL_TOKEN_DECIMALS)
                    : 0
                }
              />
            </S.BoxInGrow>
          </ContentBox>
        </S.BoxWithTitle>
      </S.FirstSection>
      <S.Section>
        <UserRewards />
      </S.Section>
    </S.Content>
  );
};
